import { useState, useEffect, useRef } from "react";
import { product } from "@/mocks/product";
import { useWhatsAppOrder } from "@/hooks/useWhatsAppOrder";

function StarIcon({ filled }: { filled: boolean }) {
  if (filled) {
    return (
      <i className="ri-star-fill w-4 h-4 flex items-center justify-center text-secondary-500 text-sm" />
    );
  }
  return (
    <i className="ri-star-line w-4 h-4 flex items-center justify-center text-secondary-500 text-sm" />
  );
}

export default function FeaturedProductSection() {
  const [selectedImage, setSelectedImage] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { openModal } = useWhatsAppOrder();

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add("animate-fade-in");
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const images = product.images;
  const thumbnailLabels = ["Front", "Back", "Side", "Detail"];

  return (
    <section ref={sectionRef} className="py-10 md:py-16 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
          {/* Left: Main image + thumbnails — constrained width */}
          <div className="flex flex-col sm:flex-row gap-4 lg:w-auto">
            {/* Main image — modest portrait size */}
            <div className="relative rounded-2xl overflow-hidden aspect-[3/4] w-full sm:w-[340px] lg:w-[360px] flex-shrink-0">
              <img
                src={images[selectedImage].src}
                alt={images[selectedImage].alt}
                className="w-full h-full object-cover object-top transition-opacity duration-300"
              />
            </div>

            {/* Thumbnail strip — vertical on desktop, horizontal on mobile */}
            <div className="flex sm:flex-col gap-2.5 sm:w-24 lg:w-28 flex-shrink-0">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`relative flex-shrink-0 w-[72px] h-[96px] sm:w-24 sm:h-32 lg:w-28 lg:h-36 rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                    selectedImage === index
                      ? "border-accent-500 ring-2 ring-accent-300/40"
                      : "border-background-200 hover:border-accent-300"
                  }`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/65 via-black/25 to-transparent px-1.5 pt-3 pb-1">
                    <span className="text-white text-[10px] font-semibold leading-tight block text-center drop-shadow-sm">
                      {thumbnailLabels[index]}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right: Product details — takes remaining space comfortably */}
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-primary-500 text-sm font-semibold tracking-wide uppercase mb-2">
              Featured Product
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-accent-800 mb-3">
              {product.name}
            </h2>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl md:text-3xl font-bold text-foreground-950">
                ₹{product.price}
              </span>
              <span className="text-lg text-foreground-400 line-through">
                ₹{product.originalPrice}
              </span>
              <span className="px-2 py-0.5 bg-secondary-100 text-secondary-900 text-xs font-semibold rounded-md">
                {product.discount}
              </span>
            </div>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
              {product.subtitle}. Soft, breathable, and designed for everyday elegance.
              Available in sizes M, L, XL, and XXL.
            </p>
            <div className="flex items-center gap-2 mb-6">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <StarIcon key={i} filled={i <= Math.floor(product.rating)} />
                ))}
              </div>
              <span className="text-sm text-foreground-600">
                {product.rating} ★ · {product.ratingCount} ratings on{" "}
                {product.ratingSource}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-start gap-3">
              <a
                href="https://rzp.io/rzp/neoroop-flower-printed-set"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-secondary-500 transition-colors whitespace-nowrap"
              >
                Buy Now
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </a>
              <button
                type="button"
                onClick={() => openModal(product.name)}
                className="inline-flex items-center gap-1.5 text-xs text-foreground-400 hover:text-secondary-600 transition-colors whitespace-nowrap sm:self-end"
              >
                <i className="ri-whatsapp-line w-3.5 h-3.5 flex items-center justify-center" />
                or order on WhatsApp
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}